<template>
  <div>
    <ClientOnly>
      <Teleport to="body">
        <ModalLayout
          v-if="showing"
          :fullscreen="fullscreen"
          :disable-scroll="disableScroll"
          :min-width="minWidth"
          :video="video"
          :no-close-btn="noCloseBtn"
          :trigger-element="triggerElement"
          @close="close"
        >
          <slot />
        </ModalLayout>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<script setup>
import { watch, onMounted, onBeforeUnmount } from 'vue';
import ModalLayout from '@/components/common/ModalLayout';

const props = defineProps({
  showing: {
    required: true,
    type: Boolean,
  },
  fullscreen: {
    type: Boolean,
    default: false,
  },
  hiddenPrerender: {
    type: Boolean,
    default: false,
  },
  disableScroll: {
    type: Boolean,
    default: false,
  },
  minWidth: {
    type: Boolean,
    default: false,
  },
  video: {
    type: Boolean,
    default: false,
  },
  noCloseBtn: {
    type: Boolean,
    default: false,
  },
  triggerElement: {
    type: [Object, null],
    default: null,
    validator: (value) => {
      if (typeof window === 'undefined') return true;
      return value === null || value instanceof HTMLElement;
    },
  },
});

const emit = defineEmits(['close']);

const toggleModalClass = (value) => {
  if (value) {
    document.body.classList.add('modal-open');
  } else {
    document.body.classList.remove('modal-open');
  }
};

// Handle ESC key press
const handleKeydown = (event) => {
  if (event.key === 'Escape' && props.showing) {
    close();
  }
};

// Watch for changes to the showing prop
watch(
  () => props.showing,
  (newValue) => {
    toggleModalClass(newValue);

    // Add or remove ESC key event listener based on modal state
    if (newValue) {
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('keydown', handleKeydown);
    }
  }
);

// Initialize modal class if showing on mount
onMounted(() => {
  if (props.showing) {
    toggleModalClass(true);
    document.addEventListener('keydown', handleKeydown);
  }
});

// Clean up on component unmount
onBeforeUnmount(() => {
  document.body.classList.remove('modal-open');
  document.removeEventListener('keydown', handleKeydown);
});

const close = () => {
  emit('close');
};
</script>

<style>
@reference "~/assets/css/main.css";
.modal-fade-enter-active,
.modal-fade-leave-active {
  @apply transition-all delay-[400];
}
.modal-fade-enter,
.modal-fade-leave-to {
  @apply opacity-0;
}

/* stop scroll on modal open */
body.modal-open {
  @apply overflow-hidden;
}

/* prevent page-header layered on top of model */
body.modal-open .page-header {
  @apply z-0!;
}
</style>
